import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import useMe from '../hooks/useMe.js'
import {
  listMyEvents, listMyBookings,
  listAdminEvents, listAdminBookings, listAdminUsers,
} from '../lib/api.js'
import { signOut } from '../lib/auth.js'
import { useAuthConfig } from '../config/appConfig.jsx'
import Avatar from '../components/Avatar.jsx'
import ErrorAlert from '../components/ErrorAlert.jsx'
import NotASpeakerYet from '../components/NotASpeakerYet.jsx'
import YearTimeline from '../components/YearTimeline.jsx'
import { formatLocalDateTime } from '../lib/datetime.js'
import { PAGE_STANDARD } from '../lib/layout.js'
import { BTN_PRIMARY } from '../lib/ui.js'

// Post-sign-in dashboard (#44). Renders a SPEAKER or ADMIN variant based on
// the resolved /me role. All data already exposed by existing endpoints —
// this page is webapp-only.
export default function DashboardPage() {
  const { me, loading } = useMe()

  if (loading) {
    return (
      <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      </main>
    )
  }

  // Either no group_member row yet (/me returned 403) or a resolved VIEWER —
  // both land here and ask the admins to promote them (#269). Previously we
  // hard-redirected VIEWERs to `/`, which silently dropped people who had
  // signed up and were trying to figure out why /admin didn't work.
  if (!me || me.role === 'VIEWER') {
    return <NotASpeakerYet email={me?.email} displayName={me?.displayName} />
  }

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          <span className="brand-gradient-text">Dashboard</span>
        </h1>
      </header>

      <ProfileCard me={me} />

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {me.role === 'ADMIN' ? <AdminPanels /> : <SpeakerPanels />}
      </div>
    </main>
  )
}

function ProfileCard({ me }) {
  const authConfig = useAuthConfig()
  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/60 sm:p-6">
      <div className="flex flex-wrap items-center gap-4">
        <Avatar email={me.email} displayName={me.displayName}
                size="h-16 w-16" textSize="text-lg" />
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold text-slate-900 truncate">{me.displayName}</div>
          <div className="text-sm text-slate-600 truncate">{me.email}</div>
          <span className="mt-1 inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-800">
            {me.role}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/events/new"
            className={BTN_PRIMARY}
          >
            New event
          </Link>
          <button
            type="button"
            onClick={() => signOut({ issuer: authConfig?.issuer })}
            className="inline-flex min-h-[44px] items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            Sign out
          </button>
        </div>
      </div>
    </section>
  )
}

function SpeakerPanels() {
  const [events, setEvents] = useState(null)
  const [bookings, setBookings] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([listMyEvents(), listMyBookings()])
      .then(([ev, bk]) => { if (!cancelled) { setEvents(ev); setBookings(bk) } })
      .catch((e) => { if (!cancelled) setError(String(e.message || e)) })
    return () => { cancelled = true }
  }, [])

  const upcoming = useMemo(() => {
    if (!events) return null
    const now = Date.now()
    return events
      .filter((e) => e.startsAtUtc && new Date(e.startsAtUtc).getTime() >= now)
      .sort((a, b) => new Date(a.startsAtUtc) - new Date(b.startsAtUtc))
      .slice(0, 3)
  }, [events])

  const pending = useMemo(
    () => (bookings ? bookings.filter((b) => b.status === 'PENDING').length : null),
    [bookings],
  )

  return (
    <>
      <ErrorAlert title="Couldn't load your dashboard" message={error}
                  source="DashboardPage(speaker)" className="lg:col-span-2" />

      <Panel title={`${new Date().getUTCFullYear()} timeline`} className="lg:col-span-2">
        {events === null ? <Skeleton rows={2} /> : <YearTimeline events={events} />}
      </Panel>

      <Panel title="Your next talks"
             actionHref="/admin/events" actionLabel="View all →">
        {upcoming === null ? (
          <Skeleton rows={3} />
        ) : upcoming.length === 0 ? (
          <Empty>No upcoming talks. <Link to="/admin/events/new" className="text-brand-700 hover:underline">Add one →</Link></Empty>
        ) : (
          <ul className="divide-y divide-slate-100">
            {upcoming.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900">{e.title}</div>
                  <div className="text-xs text-slate-500">
                    {formatLocalDateTime(e.startsAtUtc)}
                    {e.city ? ` • ${e.city}${e.country ? `, ${e.country}` : ''}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={e.status} />
                  <Link to={`/admin/events/${e.id}`}
                        className="rounded-full px-3 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-200 hover:bg-brand-50">
                    Edit
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Pending booking requests"
             actionHref="/admin/bookings" actionLabel="Review →">
        {pending === null ? (
          <Skeleton rows={1} />
        ) : pending === 0 ? (
          <Empty>No pending requests.</Empty>
        ) : (
          <div className="py-2">
            <div className="text-3xl font-bold text-slate-900">{pending}</div>
            <div className="text-sm text-slate-600">
              {pending === 1 ? 'booking is' : 'bookings are'} waiting on you.
            </div>
          </div>
        )}
      </Panel>
    </>
  )
}

function AdminPanels() {
  const [events, setEvents] = useState(null)
  const [bookings, setBookings] = useState(null)
  const [users, setUsers] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      // size=500 widens the page to the whole year for the timeline (#73);
      // the existing recent-events list still uses just the first 5.
      listAdminEvents({ includeDeleted: false, size: 500 }),
      listAdminBookings(),
      listAdminUsers(),
    ])
      .then(([ev, bk, us]) => {
        if (cancelled) return
        // listAdminEvents returns a paged envelope; listAdminBookings is a
        // bare array; listAdminUsers is a bare array. Normalize.
        setEvents(Array.isArray(ev) ? ev : (ev.content || []))
        setBookings(Array.isArray(bk) ? bk : (bk.content || []))
        setUsers(Array.isArray(us) ? us : (us.content || []))
      })
      .catch((e) => { if (!cancelled) setError(String(e.message || e)) })
    return () => { cancelled = true }
  }, [])

  const kpis = useMemo(() => {
    if (!events || !bookings || !users) return null
    const now = Date.now()
    const horizon = now + 30 * 24 * 60 * 60 * 1000
    const inWindow = (e) => {
      const t = e.startsAtUtc ? new Date(e.startsAtUtc).getTime() : null
      return t !== null && t >= now && t <= horizon
    }
    const byStatus = (s) => events.filter((e) => e.status === s).length
    return {
      total: events.length,
      published: byStatus('PUBLISHED'),
      draft: byStatus('DRAFT'),
      cancelled: byStatus('CANCELLED'),
      upcoming30: events.filter(inWindow).length,
      members: users.length,
      pendingBookings: bookings.filter((b) => b.status === 'PENDING').length,
    }
  }, [events, bookings, users])

  const recentBookings = useMemo(
    () => (bookings ? [...bookings]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 5) : null),
    [bookings],
  )
  const recentEvents = useMemo(
    () => (events ? [...events]
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
      .slice(0, 5) : null),
    [events],
  )

  return (
    <>
      <ErrorAlert title="Couldn't load admin dashboard" message={error}
                  source="DashboardPage(admin)" className="lg:col-span-2" />

      <Panel title={`${new Date().getUTCFullYear()} timeline`} className="lg:col-span-2">
        {events === null ? <Skeleton rows={2} /> : <YearTimeline events={events} />}
      </Panel>

      <Panel title="At a glance" className="lg:col-span-2">
        {kpis === null ? <Skeleton rows={2} /> : (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi label="Events"
                 value={kpis.total}
                 sub={`${kpis.published} live · ${kpis.draft} draft · ${kpis.cancelled} cancelled`} />
            <Kpi label="Next 30 days" value={kpis.upcoming30} />
            <Kpi label="Members" value={kpis.members} />
            <Kpi label="Pending bookings" value={kpis.pendingBookings} />
          </dl>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/admin/events/new"
                className={BTN_PRIMARY}>
            New event
          </Link>
          <Link to="/admin/users"
                className="inline-flex min-h-[44px] items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Manage users
          </Link>
          <Link to="/admin/bookings"
                className="inline-flex min-h-[44px] items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Manage bookings
          </Link>
          <Link to="/admin/tags"
                className="inline-flex min-h-[44px] items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Manage tags
          </Link>
          <Link to="/admin/messages"
                className="inline-flex min-h-[44px] items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Manage messages
          </Link>
        </div>
      </Panel>

      <Panel title="Recent bookings"
             actionHref="/admin/bookings" actionLabel="All bookings →">
        {recentBookings === null ? <Skeleton rows={5} /> :
         recentBookings.length === 0 ? <Empty>No bookings yet.</Empty> : (
          <ul className="divide-y divide-slate-100">
            {recentBookings.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900">{b.eventName || '(no name)'}</div>
                  <div className="text-xs text-slate-500 truncate">
                    From {b.organiserEmail || 'unknown'}
                  </div>
                </div>
                <StatusPill status={b.status} />
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Recently updated events"
             actionHref="/admin/events" actionLabel="All events →">
        {recentEvents === null ? <Skeleton rows={5} /> :
         recentEvents.length === 0 ? <Empty>No events yet.</Empty> : (
          <ul className="divide-y divide-slate-100">
            {recentEvents.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <Link to={`/admin/events/${e.id}`}
                        className="block truncate text-sm font-medium text-slate-900 hover:text-brand-700 hover:underline">
                    {e.title}
                  </Link>
                  <div className="text-xs text-slate-500">
                    {formatLocalDateTime(e.startsAtUtc)}
                  </div>
                </div>
                <StatusPill status={e.status} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  )
}

function Panel({ title, actionHref, actionLabel, className = '', children }) {
  return (
    <section className={`rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/60 sm:p-6 ${className}`}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {actionHref ? (
          <Link to={actionHref} className="text-xs font-medium text-brand-700 hover:underline">
            {actionLabel}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  )
}

function Kpi({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-slate-500">{sub}</div> : null}
    </div>
  )
}

function StatusPill({ status }) {
  const palette = ({
    PUBLISHED: 'bg-emerald-100 text-emerald-800',
    DRAFT: 'bg-slate-100 text-slate-700',
    TENTATIVE: 'bg-sky-100 text-sky-800',
    CANCELLED: 'bg-rose-100 text-rose-800',
    ARCHIVED: 'bg-slate-100 text-slate-500',
    PENDING: 'bg-amber-100 text-amber-800',
    ACCEPTED: 'bg-emerald-100 text-emerald-800',
    DECLINED: 'bg-rose-100 text-rose-800',
  })[status] || 'bg-slate-100 text-slate-700'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${palette}`}>
      {status || '—'}
    </span>
  )
}

function Skeleton({ rows = 3 }) {
  return (
    <div className="space-y-2 py-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-4 animate-pulse rounded bg-slate-100" />
      ))}
    </div>
  )
}

function Empty({ children }) {
  return <div className="py-3 text-sm text-slate-500">{children}</div>
}
